import { useEffect, useRef, useState } from 'react'

interface UseDebouncedOptionsConfig<T> {
  search: (phrase: string) => Promise<Array<T>>
  /** below this many typed characters nothing is fetched */
  minChars?: number
  delayMs?: number
}

interface UseDebouncedOptionsResult<T> {
  query: string
  setQuery: (query: string) => void
  options: Array<T>
  loading: boolean
}

/**
 * Server-side option search for autocompletes: debounced, gated on a minimum
 * phrase length, with stale responses discarded.
 */
export const useDebouncedOptions = <T>({
  search,
  minChars = 2,
  delayMs = 300,
}: UseDebouncedOptionsConfig<T>): UseDebouncedOptionsResult<T> => {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<Array<T>>([])
  const [loading, setLoading] = useState(false)
  const requestTokenRef = useRef(0)
  const searchRef = useRef(search)
  searchRef.current = search

  useEffect(() => {
    const phrase = query.trim()
    if (phrase.length < minChars) {
      requestTokenRef.current++
      setOptions([])
      setLoading(false)
      return
    }

    const token = ++requestTokenRef.current
    setLoading(true)
    const timeout = setTimeout(async () => {
      try {
        const results = await searchRef.current(phrase)
        if (token === requestTokenRef.current) setOptions(results)
      } finally {
        if (token === requestTokenRef.current) setLoading(false)
      }
    }, delayMs)

    return () => clearTimeout(timeout)
  }, [query, minChars, delayMs])

  return { query, setQuery, options, loading }
}
